import { Component, Input, ViewChild, TemplateRef, inject, computed } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbAccordionModule, NgbAccordionDirective, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  faSpinner, faInfoCircle, faChartLine, faUser,
  faCaretDown, faExternalLink
} from '@fortawesome/free-solid-svg-icons';
import { signal } from '@angular/core';
import { LtiUser } from '../../models/lti-user.interface';
import { LtiUserProfile } from '../../models/lti-user-profile.interface';
import {
  QuestionAttempt, QuestionResult, QuestionStatus,
  QuestionView, QuizReport
} from '../../models/user-detail-report-view';
import { DeliveryTimePipe } from '../../pipes/delivery-time.pipe';
import { environment } from '../../../environments/environment';
import { FormatStatusPipe } from '../../pipes/format-status.pipe';

@Component({
    selector: 'app-user-quiz-report',
    imports: [
        CommonModule,
        FontAwesomeModule,
        NgbAccordionModule,
        DeliveryTimePipe,
        FormatStatusPipe
    ],
    providers: [DatePipe],
    styleUrl: './user-quiz-report.component.scss',
    template: `
    @if (isLoading) {
      <div class="alert alert-info d-flex align-items-center" role="alert">
        <fa-icon [icon]="icons.spinner" [animation]="'spin'" class="me-2"></fa-icon>
        <div>Loading user quiz report...</div>
      </div>
    } @else if (!user) {
      <div class="alert alert-warning d-flex align-items-center" role="alert">
        <fa-icon [icon]="icons.infoCircle" class="me-2"></fa-icon>
        <div>User profile not found.</div>
      </div>
    } @else {
      <div class="card mb-4">
        <div class="card-header d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <fa-icon [icon]="icons.user" class="me-2"></fa-icon>
            <h5 class="mb-0">User Profile</h5>
          </div>
          <button class="btn btn-link"
                  type="button"
                  (click)="toggleProfileDetails()">
            {{ showProfileDetails() ? 'Hide Details' : 'Show Details' }}
          </button>
        </div>

        @if (showProfileDetails()) {
          <div class="card-body">
            <div class="table-responsive">
              <table class="table">
                <tbody>
                  <tr>
                    <th scope="row">Email</th>
                    <td>{{ user.email }}</td>
                    <th scope="row">Name</th>
                    <td>
                      @if (profile) {
                        {{ profile.last_name }}, {{ profile.first_name }}
                      } @else {
                        ---
                      }
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Organization</th>
                    <td>{{ profile?.organization || '---' }}</td>
                    <th scope="row">Department</th>
                    <td>{{ profile?.department || '---' }}</td>
                  </tr>
                  <tr>
                    <th scope="row">Phone</th>
                    <td>{{ profile?.phone || '---' }}</td>
                    <th scope="row">Timezone</th>
                    <td>{{ profile?.timezone || '---' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        }
      </div>

      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <fa-icon [icon]="icons.chartLine" class="me-2"></fa-icon>
            <h5 class="mb-0">Quiz Report</h5>
          </div>
        </div>
        <div class="card-body">
          @if (!hasQuizData) {
            <div class="alert alert-info mb-0">
              No quiz results available.
            </div>
          } @else {
            <div class="mb-4">
              <h5>Enrollment Information</h5>
              <div class="table-responsive">
                <table class="table">
                  <tbody>
                    <tr>
                      <th scope="row" class="w-25">Start Date</th>
                      <td>{{ quizReport.enrollment_date | date:'MMM d, y, h:mm a' }}</td>
                    </tr>
                    @if (quizReport.unenrollment_date) {
                      <tr>
                        <th scope="row">Unenrollment Date</th>
                        <td>{{ quizReport.unenrollment_date | date:'MMM d, y, h:mm a' }}</td>
                      </tr>
                    }
                    @if (quizReport.received_last_chance_date) {
                      <tr>
                        <th scope="row">Last Chance Email Date</th>
                        <td>{{ quizReport.received_last_chance_date | date:'MMM d, y, h:mm a' }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>

            <h5>Questions</h5>
            <div class="table-responsive mt-3">
              <table class="table table-bordered">
                <thead class="table-light">
                  <tr>
                    <th class="col-title">Question Title</th>
                    <th class="col-status">Status</th>
                    <th class="col-sent">Sent On</th>
                    <th class="col-attempts">Attempts</th>
                    <th class="col-views">Views</th>
                    <th class="col-link">Link</th>
                  </tr>
                </thead>
                <tbody>
                  @for (question of quizReport.questions; track question.name) {
                    <tr>
                      <td>{{ question.name }}</td>
                      <td>
                        <div [class]="getStatusBadgeClass(question.status)">
                          @if (question.status === 'Pending' && question.expected_send_date) {
                            @if (quizReport.unenrollment_date) {
                              <span>Unenrolled - never sent</span>
                            }
                            @else {
                              Pending - Expected {{ question.expected_send_date | date:'shortDate' }}
                            }
                          } @else {
                            {{ question | formatStatus }}
                          }
                        </div>
                      </td>
                      <td>
                        @if (question.sent_on && question.status !== 'Pending') {
                          {{ question.sent_on | deliveryTime:profile?.timezone:'full' }}
                        }
                      </td>
                      <td>
                        @if (question.attempts.length) {
                          @for (attempt of question.attempts; track attempt.date) {
                            <div class="mb-1">
                              <div [class]="getAttemptBadgeClass(attempt)">
                                {{ attempt.date | deliveryTime:profile?.timezone:'full' }}
                              </div>
                            </div>
                          }
                        }
                      </td>
                      <td>
                        @if (question.views.length) {
                          {{ question.views[0].viewed_at | deliveryTime:profile?.timezone:'full' }}
                          @if (question.views.length > 1) {
                            <button class="btn btn-link btn-sm p-0 ms-2"
                                    (click)="showViewsModal(question)">
                              more...
                            </button>
                          }
                        }
                      </td>
                      <td>
                        @if (question.url_slug) {
                          <a [href]="getQuestionUrl(question)"
                             target="_blank"
                             class="btn btn-link btn-sm">
                            <fa-icon [icon]="icons.externalLink"></fa-icon>
                            View
                          </a>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      </div>
    }

    <ng-template #viewsModal let-modal>
      <div class="modal-header">
        <h5 class="modal-title" id="modal-title">Question Views</h5>
        <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
      </div>
      <div class="modal-body">
        <div class="list-group">
          @for (view of selectedQuestionViews(); track view.viewed_at) {
            <div class="list-group-item">
              {{ view.viewed_at | deliveryTime:profile?.timezone:'full' }}
            </div>
          }
        </div>
      </div>
    </ng-template>
  `
})
export class UserQuizReportComponent {
  @Input() isLoading = false;
  @Input() user: LtiUser | null = null;
  @Input() profile: LtiUserProfile | null = null;
  @Input() quizReport: QuizReport = { enrollment_date: '', questions: [] };
  @Input() hasQuizData = false;

  @ViewChild('viewsModal') viewsModal!: TemplateRef<any>;

  private modalService = inject(NgbModal);
  private datePipe = inject(DatePipe);

  // Protected icons for template use
  protected readonly icons = {
    spinner: faSpinner,
    infoCircle: faInfoCircle,
    chartLine: faChartLine,
    user: faUser,
    caretDown: faCaretDown,
    externalLink: faExternalLink
  };

  // Component state
  showProfileDetails = signal<boolean>(true);
  selectedQuestionViews = signal<QuestionView[]>([]);

  toggleProfileDetails(): void {
    this.showProfileDetails.update(v => !v);
  }

  getStatusBadgeClass(status: QuestionStatus): string {
    const baseClasses = 'p-2 text-white text-center rounded';
    switch (status) {
      case 'Pending': return `${baseClasses} bg-secondary`;
      case 'Ready': return `${baseClasses} bg-primary`;
      case 'Expired': return `${baseClasses} bg-danger`;
      case 'AnsweredCorrectly': return `${baseClasses} bg-success`;
      case 'AnsweredIncorrectly': return `${baseClasses} bg-danger`;
      case 'Unknown': return `${baseClasses} bg-warning`;
      default: return baseClasses;
    }
  }

  getAttemptBadgeClass(attempt: QuestionAttempt): string {
    return `p-2 text-white text-center rounded ${attempt.is_correct ? 'bg-success' : 'bg-danger'}`;
  }

  showViewsModal(question: QuestionResult): void {
    this.selectedQuestionViews.set(
      [...question.views].sort((a, b) =>
        new Date(b.viewed_at).getTime() - new Date(a.viewed_at).getTime()
      )
    );

    this.modalService.open(this.viewsModal, {
      ariaLabelledBy: 'modal-title',
      size: 'lg'
    });
  }

  getQuestionUrl(question: QuestionResult): string {
    return question.url_slug
      ? `${environment.questionUiUrl}/question/${question.url_slug}?countView=false`
      : '#';
  }
}
